import { useCallback, useState, useEffect } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import type { SigninCheckResult } from 'reactfire'
import type { Temporal } from '@js-temporal/polyfill'
import { useAuthQuery } from '~/queries/useauthquery'
import { currentUserDataQuery } from '~/queries/accounts'

export type ShowingFormState =
	| {
			step: 'time'
	  }
	| {
			showing_time: Temporal.ZonedDateTime | null
			step: 'contact'
	  }
	| {
			showing_time: Temporal.ZonedDateTime | null
			step: 'phone_number_input'
	  }
	| {
			showing_time: Temporal.ZonedDateTime | null
			step: 'success'
	  }

interface UseShowingRequestFormType {
	state: ShowingFormState
	setState: Dispatch<SetStateAction<ShowingFormState>>
	currentStep: number
	lastStep: number | undefined
	goToNextStep: () => void
	goToPreviousStep: () => void
	isSignUpFormShown: boolean
	showSignUpForm: () => void
}

export function useShowingRequestForm(
	signInStatus: 'loading' | 'error' | 'success',
	signInCheckResult: SigninCheckResult
): UseShowingRequestFormType {
	const [state, setState] = useState<ShowingFormState>({ step: 'time' })
	const currentUser = useAuthQuery(currentUserDataQuery)
	const [isSignUpFormShown, setIsSignUpFormShown] = useState(false)
	const showSignUpForm = useCallback(() => {
		setIsSignUpFormShown(true)
	}, [])
	const [currentStep, setCurrentStep] = useState(1)
	const [lastStep, setLastStep] = useState<number>()
	const [hasLastStepSet, setHasLastStepSet] = useState(false)
	useEffect(() => {
		if (
			!hasLastStepSet &&
			signInStatus == 'success' &&
			signInCheckResult &&
			!currentUser.isError &&
			!currentUser.isLoading
		) {
			setLastStep(
				signInCheckResult.signedIn && currentUser.data?.phone_number ? 2 : 3
			)
			setHasLastStepSet(true)
		}
	}, [signInStatus, signInCheckResult, hasLastStepSet, currentUser])
	const goToNextStep = useCallback(
		() => setCurrentStep((prevStep) => prevStep + 1),
		[]
	)
	const goToPreviousStep = useCallback(() => {
		if (state.step == 'contact') {
			if (isSignUpFormShown) {
				setIsSignUpFormShown(false)
			} else {
				setState({ step: 'time' })
				setCurrentStep((prevStep) => prevStep - 1)
			}
		}
	}, [state.step, isSignUpFormShown])

	return {
		state,
		setState,
		currentStep,
		lastStep,
		goToNextStep,
		goToPreviousStep,
		isSignUpFormShown,
		showSignUpForm,
	}
}
